import {
  FUSION_ASSISTANT_PROMPT_GENERIC,
  FUSION_ASSISTANT_PROMPT_PREFIX,
  FUSION_ASSISTANT_PROMPT_SUFFIX,
} from './constants';

/**
 * Whatever the page knows about the thing the writer is looking at. Every field
 * is optional: list pages have none of it, detail pages have most of it, and a
 * package row has only the package.
 */
export interface FusionAssistantContext {
  launchId?: string;
  taskName?: string;
  groupId?: string;
  groupName?: string;
  packages?: { packageName: string; commitId?: string }[];
  promptPrefix?: string;
}

/**
 * Upper bound on how many packages are named in the prompt. The prompt travels
 * to AgentSpaces in a query string, so an unbounded package list on a large
 * group could push the URL past what browsers and proxies accept.
 */
export const MAX_PROMPT_PACKAGES = 10;

const formatPackage = (pkg: {
  packageName: string;
  commitId?: string;
}): string =>
  pkg.commitId ? `${pkg.packageName}@${pkg.commitId}` : pkg.packageName;

/**
 * Composes the prompt the Fusion Assistant is seeded with. This is the only
 * place in the app that builds a Fusion Assistant prompt string.
 */
export const buildFusionAssistantPrompt = (
  context?: FusionAssistantContext
): string => {
  const { launchId, taskName, groupId, groupName } = context ?? {};
  const packages = context?.packages ?? [];
  const groupSubject = groupName ?? groupId;
  const prefix = context?.promptPrefix ?? FUSION_ASSISTANT_PROMPT_PREFIX;

  if (!launchId && !groupSubject && packages.length === 0) {
    return FUSION_ASSISTANT_PROMPT_GENERIC;
  }

  let subjectClause = '';
  if (launchId) {
    subjectClause = ` for task ${launchId}`;
    if (taskName) {
      subjectClause += ` (${taskName})`;
    }
  } else if (groupSubject) {
    subjectClause = ` for group ${groupSubject}`;
  }

  // Use the same groupName-then-groupId fallback as the subject so a task that
  // knows only the group's id (no name) still names the group in the prompt.
  const groupClause =
    launchId && groupSubject ? `, in group ${groupSubject}` : '';

  let packagesClause = '';
  if (packages.length > 0) {
    const named = packages.slice(0, MAX_PROMPT_PACKAGES).map(formatPackage);
    const overflow = packages.length - named.length;
    packagesClause = `, involving package(s) ${named.join(', ')}${
      overflow > 0 ? ` and ${overflow} more` : ''
    }`;
  }

  return `${prefix}${subjectClause}${groupClause}${packagesClause}${FUSION_ASSISTANT_PROMPT_SUFFIX}`;
};
